import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

function ComplexPolymerAttachmentDetails(): JSX.Element {
  return (
    <div className={css(styles.attachmentDetails)}>
      <Row>
        <Col md={4}>1. Select atoms in structure.</Col>
        <Col md={4}> 2. Right click on atoms for atom properties.</Col>
        <Col md={4}>3. Define atom mapping. Unlock the atom mapping property by clicking on the lock.</Col>
      </Row>
      <Row>
        <Col md={4}>
          <img src={staticURL('/static/img/atom_mapping_example_molecule.svg')} />
        </Col>
        <Col md={4}>
          <img src={staticURL('/static/img/atom_mapping_instructions_pane_1.svg')} />
        </Col>
        <Col md={4}>
          <img src={staticURL('/static/img/atom_mapping_instructions_pane_2.svg')} />
        </Col>
      </Row>
    </div>
  );
}

function ComplexPolymerAttachmentBanner(props: {showAttachmentPointBanner: boolean}): JSX.Element | null {
  const [showDetails, setShowDetails] = useState(false);
  const [hasDismissedBanner, setHasDismissedBanner] = useState(false);
  const shouldShowBanner =
    props.showAttachmentPointBanner &&
    BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES &&
    !hasDismissedBanner;
  return shouldShowBanner ? (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text="Define leaving groups for complex polymer attachments by enumerating atoms."
      supportingInfo={showDetails ? <ComplexPolymerAttachmentDetails /> : null}
      action={{
        actionType: InlineBannerActionType.CUSTOM,
        actionText: 'Show details',
        actionOnClick: () => {
          setShowDetails(!showDetails);
        },
      }}
      onDismiss={() => {
        setHasDismissedBanner(true);
      }}
    />
  ) : null;
}

export interface ChemicalStructureModalInputProps {
  initialSketcherValue: ChemicalStructureDrawerValue;
  onSketcherValueChange: (sketcherValue: ChemicalStructureDrawerValue) => void;
  mode: ChemicalStructureDrawerMode;
  setHasReactionContents?: (isReaction: boolean) => void;
  inputFormat?: MarvinChemicalFormat | null;
  outputFormat?: MarvinChemicalFormat;
  showAttachmentPointBanner?: boolean;
}

export default function ChemicalStructureModalInput({
  initialSketcherValue,
  onSketcherValueChange,
  setHasReactionContents,
  mode,
  inputFormat,
  outputFormat,
  showAttachmentPointBanner = true,
}: ChemicalStructureModalInputProps): JSX.Element {
  return (
    <div className={css(styles.wrapper)}>
      <ComplexPolymerAttachmentBanner showAttachmentPointBanner={showAttachmentPointBanner} />
      <div className={css(styles.sketcher)}>
        <ChemicalStructureDrawer
          initialValue={initialSketcherValue}
          onMoleculeChange={onSketcherValueChange}
          setHasReactionContents={setHasReactionContents}
          mode={mode}
          inputFormat={inputFormat}
          outputFormat={outputFormat}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  sketcher: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'stretch',
    flexDirection: 'row',
    flex: 1,
    minHeight: 350,
  },
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  attachmentDetails: {
    ...FONT_PROXIMA_TITLE,
    paddingTop: 10,
  },
});
